/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 *
 *  @author estelle.giuly
 *
 */

import {Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';

import {
  FormModel,
  FormOutcome,
  HistoricProcessInstanceResponse,
  ProcessDefinition,
  ProcessDefinitions,
  ProcessInstance,
  ProcessInstancesResponse,
  Request,
  Task,
  TasksResponse,
  Variable
} from './workflow-v2.interface';

import {ObserveType, ProfileMode, REQUEST_OPTIONS} from '../../app.constants';
import {TasksService} from '../../inbox/workflows/services/tasks.service';
import {ApiService} from '../api';

export const HR_ABSENCE_WORKFLOW = 'hrAbsence';
export const ESS_ABSENCE_WORKFLOW = 'essAbsence';
export const ESS_CLAIM_WORKFLOW = 'essClaim';
export const UPDATE_PERSONAL_INFO_WORKFLOW = 'updatePersonalInfo';
export const TERMINATE_CONTRACT_WORKFLOW = 'terminateContract';
export const ONBOARDING_WORKFLOW = 'onboarding';
export const UPDATE_MSS_SUBORDINATE_INFO_WORKFLOW = 'updateMssSubordinateInfo';
export const SEND_DOCUMENT = 'sendDocument';
export const CLI_REG_SUFFIX = '_cliReg-';
export const REQUEST_TITLE_TRANSLATE_PREFIX = 'request_title_';
export const FORM_TRANSLATE_PREFIX = 'form_';
export const FORM_OUTCOME_TRANSLATE_PREFIX = 'form_outcome_';
export const FORM_PLACEHOLDER_TRANSLATE_PREFIX = 'form_placeholder_';
export const ONBOARDING_TASK_DEFINITION_KEY = 'validateOnboardingForm';
export const NEW_ONBOARDING_TASK_DEFINITION_KEY = 'managerValidation';
export const NEW_ONBOARDING_WORKFLOW_TYPE = 'new_onboarding';

export const getWorkflowKey = (workflowName: string, cliRegId: number): string => workflowName + CLI_REG_SUFFIX + cliRegId;
export const getWorkflowName = (workflowKey: string): string => workflowKey.indexOf(CLI_REG_SUFFIX) > -1 ?
  workflowKey.substr(0, workflowKey.indexOf(CLI_REG_SUFFIX)) : workflowKey;

export const tasks_per_page = 20;
export const requests_per_page = 20;
export const workflows_per_page = 25;
const defaultQueryParams = {'per-page': tasks_per_page, page: 0, search: 'status==IN_PROGRESS'};

@Injectable()
export class WorkflowV2Service {

  constructor(private api: ApiService,
              private tasksService: TasksService
  ) {
  }

  public getProcessDefinitions(queryParams: any): Observable<ProcessDefinitions> {
    return this.api.get(`/workflowable/process-api/repository/process-definitions`, {
      requestOptions: {
        params: queryParams
      }
    });
  }

  public getLatestProcessDefinitionByKey(key: string): Observable<ProcessDefinition> {
    const queryParams = {
      latest: true,
      key
    };
    return this.getProcessDefinitions(queryParams)
      .pipe(
        filter((processDefinitions) => !!processDefinitions.size),
        map((processDefinitions) => processDefinitions.data[0])
      );
  }

  public getRequests(queryParams: any): Observable<ProcessInstancesResponse> {
    return this.api.get(`/workflowable/my-requests`, {
      requestOptions: {
        params: {
          ...queryParams
        },
        observe: ObserveType.RESPONSE
      }
    }).pipe(
      map((response) => ({
        data: response.body,
        total: +response.headers.get('X-Total-Count')
      }))
    );
  }

  public getOngoingRequests(queryParams: any): Observable<ProcessInstancesResponse> {
    return this.api.get(`/workflowable/ongoing-requests`, {
      requestOptions: {
        params: {
          ...queryParams
        },
        observe: ObserveType.RESPONSE
      }
    }).pipe(
      map((response) => ({
        data: response.body
      }))
    );
  }

  public startProcessInstance(key: string, variables?: any, isSilent?: boolean, profileMode = ProfileMode.CLASSIC)
    : Observable<ProcessInstance> {
    const body = {
      key,
      variables: variables ? this.convertVariablesMapToWorkflowVariables(variables, 'global') : null
    };

    const api = isSilent ? this.api.silent : this.api;
    return api.post(`/workflowable/requests?profile-mode=${profileMode}`, body);
  }

  public startProcessInstanceWithForm(workflowKey: string,
                                      formModel: FormModel,
                                      outcome?: FormOutcome,
                                      variables?: any,
                                      profileMode = ProfileMode.CLASSIC
  ): Observable<Response> {
    const formVariables = this.convertFormModelToVariablesMap(formModel);
    const body = {
      key: workflowKey,
      startFormVariables: variables ? this.convertVariablesMapToWorkflowVariables(formVariables, 'global') : null,
      variables: variables ? this.convertVariablesMapToWorkflowVariables(variables, 'global') : null,
      outcome: outcome ? (outcome.id ? outcome.id : outcome.name) : null
    };
    return this.api.post(`/workflowable/requests?profile-mode=${profileMode}`, body);
  }

  public getHistoricRequests(queryParams: any): Observable<HistoricProcessInstanceResponse> {
    return this.api.get(`/workflowable/my-historic-requests`, {
      requestOptions: {
        params: {
          ...queryParams
        },
        observe: ObserveType.RESPONSE
      }
    }).pipe(
      map((response) => ({
        data: response.body,
        total: +response.headers.get('X-Total-Count')
      }))
    );
  }

  public getPendingRequests(contractId: number): Observable<any[]> {
    return this.api.get(`/workflowable/pending-requests?contractId=${contractId}`);
  }

  public cancelRequest(request: Request) {
    return this.api.post(`/workflowable/requests/${request.id}/cancel`);
  }

  public getTasks(queryParams: any = defaultQueryParams): Observable<TasksResponse> {
    return this.api.get(`/workflowable/my-tasks`, {
      requestOptions: {
        params: {
          ...queryParams
        },
        observe: ObserveType.RESPONSE
      }
    }).pipe(
      map((response) => ({
        data: response.body.map(task => {
          const isFallbackRecipient = task.variables
            .some(variable => variable.name === 'isFallbackRecipient' && variable.value === true);
          return {...task, isFallbackRecipient};
        }),
        total: +response.headers.get('X-Total-Count')
      }))
    );
  }

  public getTaskById(taskId: string): Observable<Task> {
    return this.api.get(`/workflowable/tasks/${taskId}`);
  }

  public completeTask(taskId: string, taskDefinitionKey: string, variables: any): Observable<Response> {
    return this.api.post(`/workflowable/tasks/${taskId}/complete`, {
      variables: [{
        name: taskDefinitionKey,
        scope: 'global',
        value: variables
      }]
    });
  }

  // TODO Improve
  public completeTaskWithForm(taskId: string, formModel: FormModel, outcome?: FormOutcome): Observable<Response> {
    const formVariables = this.convertFormModelToVariablesMap(formModel);
    const outcomeValue = outcome ? (outcome.id ? outcome.id : outcome.name) : null;
    const body = {
      formDefinitionId: formModel.id,
      variables: this.convertVariablesMapToWorkflowVariables(formVariables, 'global'),
      outcome: outcomeValue
    };

    return this.api.post(`/workflowable/tasks/${taskId}/complete`, body);
  }

  // TODO Fix subscribe
  public completeTaskAndNotify(taskId: string, taskDefinitionKey: string, variables: any, notificationId: number,
                               reply: string): Observable<Response> {
    return this.completeTask(taskId, taskDefinitionKey, variables)
      .pipe(
        tap({
          next: () => this.updateManagerReply(notificationId, reply).subscribe(),
          error: () => console.error('Failed to complete workflowV2 user task #' + taskId)
        })
      );
  }

  public getTaskCompletionStatus(taskId: string, outcome: string): Observable<Response> {
    return this.api.silent.get(`/workflowable/tasks/${taskId}/complete?outcome=${outcome}`);
  }

  public getLatestFormModelByFormKey(formKey: string): Observable<FormModel> {
    return this.api.get(`/workflowable/form-model/${formKey}`).pipe(
      catchError(() => {
        console.error(`Failed to get form model with formKey ${formKey}.`);
        return of(null);
      }));
  }

  public convertFormModelToVariablesMap(formModel: FormModel): any {
    return formModel.fields.reduce((formVariables, field) => {
      formVariables[field.id] = field.value;
      return formVariables;
    }, {});
  }

  public convertVariablesMapToWorkflowVariables(variables: any, scope: 'global' | 'local'): Variable[] {
    return Object.keys(variables).map((key) => ({name: key, scope, value: variables[key]}));
  }

  public updateManagerReply(notificationId: number, reply: string): Observable<Response> {
    return this.api.post(`/communication/api/v1/userNotification/updateManagerReply/${notificationId}`, reply);
  }

  public getWorkflowsInstances(page: number, perPage: number = workflows_per_page, search: string = '')
    : Observable<{ total: number, isLimitExceeded: boolean, data: Request[] }> {
    return this.api.get(`/workflowable/admin/requests?per-page=${perPage}&page=${page}&search=${search}`,
      REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => ({
          total: response.headers.get('x-total-count') || 0,
          isLimitExceeded: response.headers.get('x-response-code') === 'CONTRACT_LIMIT_EXCEEDED',
          data: response.body.map(request => ({
            ...request,
            instanceShortId: request.id.substr(0, request.id.indexOf('-'))
          }))
        })),
        catchError(() => of({total: 0, isLimitExceeded: false, data: []}))
      );
  }

  public getWorkflowTypes(): Observable<string[]> {
    return this.api.silent.get(`/workflowable/request-types`);
  }

  public getRequestDetails(requestId: string): Observable<Request> {
    return this.api.get(`/workflowable/admin/requests/${requestId}`);
  }

  public reassignRequest(id: string, assignee: string) {
    return this.api.post(`/workflowable/admin/requests/${id}/assign`, {
      assignee
    });
  }

  public reassignTask(id: string, assignee: string) {
    return this.api.post(`/workflowable/tasks/${id}/assign`, {
      assignee
    });
  }

  public getWorkflowsByStatusChartData(): Observable<any> {
    return this.api.get(`/workflowable/stats/admin/requests/count-by-status`);
  }

  public getWorkflowsByTypeChartData(): Observable<any> {
    return this.api.get(`/workflowable/stats/admin/requests/count-by-type`);
  }

  public getOldWorkflowsData(): Observable<any> {
    return this.api.get(`/workflowable/stats/admin/requests/pending-more-than-one-month`);
  }

  public cancelWorkflow(requestId: string, comment: string): Observable<Response> {
    return this.api.post(`/workflowable/requests/${requestId}/cancel`, {
      comment
    });
  }
}

